import * as THREE from 'three';
import { createDiorama, type Diorama } from './diorama';
import { PixelPipeline } from './pixelate';
import { createGardenView, type GardenView } from './garden_view';
import { createCustomerView, type CustomerView } from './customer_view';
import { createIslandVisitors, type IslandVisitors } from './island_visitors';
import { createCarousel, type IslandCarousel } from './carousel';
import { createPicker, type Picker } from './picker';

/**
 * 描画アプリの起動。レンダラ生成・リサイズ・RAF ループを束ねる。
 * ロジック（sim）には一切触れず、毎フレームの累計秒だけを onFrame に渡す。
 *
 * M2 から庭ビュー（デコ描画）とピッカー（タップ→セル変換）も束ねる。
 */
export interface RenderApp {
  diorama: Diorama;
  /** 庭デコの描画ビュー（main が GameState.garden を sync する）。 */
  garden: GardenView;
  /** 客（来店者）の描画ビュー（main が来店イベントで spawn する）。 */
  customers: CustomerView;
  /** 季節別館（拡張島）の軽い来客演出（v0.8.5）。 */
  islandVisitors: IslandVisitors;
  /** 島カルーセル（隣島の見切れディスク。v0.7.2）。 */
  carousel: IslandCarousel;
  /** ポインタ座標 → グリッドセルの変換器。 */
  picker: Picker;
  /** レンダラのキャンバス（ポインタイベントの登録先）。 */
  canvas: HTMLCanvasElement;
  /** 各フレームのコールバック（累計秒）。HUD 更新などに使う。 */
  onFrame?: (tSec: number) => void;
  dispose(): void;
}

// ドット絵の粒度。低解像度 RT のベース高さ。アスペクトから幅を決める。
const LOW_RES_HEIGHT = 270;

export function startRender(container: HTMLElement): RenderApp {
  const renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'low-power' });
  renderer.setPixelRatio(1); // ピクセレートが本体なので devicePixelRatio は使わない
  renderer.setClearColor(0x000000, 0);
  container.appendChild(renderer.domElement);

  const diorama = createDiorama();
  const pipeline = new PixelPipeline(renderer, 480, LOW_RES_HEIGHT);

  // 庭ビューを island（自転ノード）に乗せ、デコがグリッドと一緒に回るようにする。
  const garden = createGardenView();
  diorama.island.add(garden.root);

  // 客ビューも island に乗せる（庭と一緒に回り、座標系を共有する）。
  const customers = createCustomerView();
  diorama.island.add(customers.root);

  // 拡張島の ambient 来客（季節の常連）も island に乗せる（島と一緒に回る）。
  const islandVisitors = createIslandVisitors();
  diorama.island.add(islandVisitors.root);

  // 島カルーセル（隣島の見切れディスク）。scene 直下に乗せる（フォーカス島の自転とは独立）。
  const carousel = createCarousel();
  diorama.scene.add(carousel.root);

  const picker = createPicker(renderer.domElement, diorama.camera, diorama.pickPlane, diorama.cafe);

  const app: RenderApp = {
    diorama,
    garden,
    customers,
    islandVisitors,
    carousel,
    picker,
    canvas: renderer.domElement,
    dispose,
  };

  function resize(): void {
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;
    renderer.setSize(w, h, false);
    diorama.resize(w, h);

    // 低解像度バッファのアスペクトを画面に合わせる（ピクセルを正方に保つ）
    const aspect = w / h;
    pipeline.setLowResolution(Math.round(LOW_RES_HEIGHT * aspect), LOW_RES_HEIGHT);
  }

  resize();
  window.addEventListener('resize', resize);

  const clock = new THREE.Clock();
  let raf = 0;

  function loop(): void {
    const tSec = clock.getElapsedTime();
    diorama.update(tSec);
    garden.update(tSec);
    customers.update(tSec);
    islandVisitors.update(tSec);
    carousel.update(tSec);
    pipeline.render(diorama.scene, diorama.camera);
    app.onFrame?.(tSec);
    raf = requestAnimationFrame(loop);
  }
  raf = requestAnimationFrame(loop);

  function dispose(): void {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
    garden.dispose();
    customers.dispose();
    islandVisitors.dispose();
    carousel.dispose();
    pipeline.dispose();
    renderer.dispose();
    renderer.domElement.remove();
  }

  return app;
}
